import { requireService, wrap } from '@repo/di-ez';
import { Table } from '@repo/go-table';
import type { GridApi, GridColDef, GridColumnResizeParams } from '@repo/go-table';
import { hasValue } from '@repo/utils';
import { useCallback, useEffect, useMemo } from 'react';
import type { MutableRefObject } from 'react';
import { debounce } from 'throttle-debounce';

import type { FactTrajectoryPointEntity } from '../../service/entities';
import { transformAppLanguageToTableLanguageToken } from '../../utils/table-language';

import { SERVICE_COLUMNS } from './display-manager';
import { useTableEventHandlers } from './hooks/use-table-event-handlers';
import {
  isColumnsOrderModelEqual,
  isValidColumnsOrderModel,
  mergeColumnsOrderModel,
} from './utils/columns-order-model';

type Props = {
  language: string;
  columns: GridColDef[];
  tableApi: MutableRefObject<GridApi>;
};

export const TrajectoryTable = wrap<Props>(function TrajectoryTable({ language, columns, tableApi }) {
  const trajectoryService = requireService('trajectoryService');
  const uiStore = requireService('trajectoryUiStore');
  const displayManager = uiStore.displayManager;

  const tableLanguage = useMemo(() => transformAppLanguageToTableLanguageToken(language), [language]);

  const columnFields = useMemo(() => columns.map((column) => column.field), [columns]);
  const { mainFactTrajectory } = trajectoryService;
  const { onCopy } = useTableEventHandlers({ apiRef: tableApi });

  const columnsOrderModel = displayManager.columnsOrderModel;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleWidthChange = useCallback(
    debounce(300, ({ colDef: { field }, width }: GridColumnResizeParams) => {
      const tableColumns = tableApi.current.getAllColumns();
      const updatedColumns: Record<string, number> = {};

      tableColumns.forEach((column) => {
        if (column.field === field) {
          updatedColumns[field] = width;
        } else if (displayManager.settings.columnsWidth && !displayManager.settings.columnsWidth[column.field]) {
          updatedColumns[column.field] = column.computedWidth;
        }
      });

      displayManager.onWidthChange(updatedColumns);
    }),
    [displayManager],
  );

  const handleColumnsOrderModelChange = useCallback(
    (orderModel: string[]) => {
      const isOrderModelValid =
        orderModel.length === columnFields.length && orderModel.every((field) => columnFields.includes(field));

      if (!isOrderModelValid) {
        return;
      }

      const hasSavedOrderModel = Boolean(columnsOrderModel?.length);
      const hasCustomSavedOrderModel = hasSavedOrderModel && !isColumnsOrderModelEqual(columnsOrderModel, columnFields);
      const isIncomingDefaultOrderModel = isColumnsOrderModelEqual(orderModel, columnFields);

      // Mount/rebuild noise from go-table: keep custom order, ignore default overwrite.
      if (hasCustomSavedOrderModel && isIncomingDefaultOrderModel) {
        return;
      }

      displayManager.onColumnsOrderModelChange(orderModel);
    },
    [columnFields, columnsOrderModel, displayManager],
  );

  useEffect(() => {
    return tableApi.current.subscribeEvent('columnSeparatorDoubleClick', ({ field }) => {
      const col = tableApi.current.getColumn(field);
      const width = col.width;

      if (hasValue(width)) {
        handleWidthChange({ colDef: col, width });
      }
    });
  }, [handleWidthChange, tableApi]);

  useEffect(() => {
    if (!columnFields.length) {
      return;
    }

    const saved = displayManager.columnsOrderModel;

    if (!saved?.length) {
      displayManager.onColumnsOrderModelChange(columnFields);
      return;
    }

    if (!isValidColumnsOrderModel(saved, columns)) {
      displayManager.onColumnsOrderModelChange(mergeColumnsOrderModel(saved, columnFields));
    }
  }, [columnFields, columns, displayManager]);

  useEffect(() => {
    if (!displayManager.settings.columnsWidth) {
      return;
    }

    for (const column of tableApi.current.getAllColumns()) {
      if (displayManager.settings.columnsWidth[column.field] === column.width) {
        continue;
      }

      tableApi.current.setColumnWidth(column.field, displayManager.settings.columnsWidth[column.field]);
    }
  }, [tableApi, displayManager]);

  return (
    <Table<FactTrajectoryPointEntity>
      initialState={{
        sorting: {
          sortModel: displayManager.sortingModel,
        },
        columns: {
          columnVisibilityModel: displayManager.columnsVisibilityModel,
        },
        pinnedColumns: displayManager.pinnedColumns,
      }}
      sortModel={displayManager.sortingModel}
      columnVisibilityModel={displayManager.columnsVisibilityModel}
      columnsOrderModel={displayManager.columnsOrderModel}
      onColumnVisibilityModelChange={displayManager.onColumnVisibilityChange}
      onColumnsOrderModelChange={handleColumnsOrderModelChange}
      onPinnedColumnsChange={displayManager.onPinChange}
      onSortModelChange={displayManager.onSortChange}
      onColumnResize={handleWidthChange}
      checkboxSelection={uiStore.isTableCheckboxesVisible}
      cellSelection
      disableClipboardPaste
      onClipboardCopy={onCopy}
      serviceColumns={SERVICE_COLUMNS}
      getRowId={(row) => row.entityId}
      apiRef={tableApi}
      columns={columns}
      rows={mainFactTrajectory?.points ?? []}
      language={tableLanguage}
    />
  );
});
